import React from 'react';
import { formatDate } from '@/utils/dateFormatters';
import { Badge } from '@/components/ui/badge';
import { formatEuro, safeFormatDate } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export interface Contract {
  title: string;
  value: number;
  currency: string;
  buyer: string;
  date: string;
  duration: string;
  country?: string;
  cpv?: string;
  cpvDescription?: string;
  status?: string;
  id?: string;
}

interface TopContractsTableProps {
  contracts: Contract[];
  showValueFirst?: boolean;
  showLocation?: boolean;
  showDuration?: boolean;
  showStatus?: boolean;
  title?: string;
  onRowClick?: (contractId: string) => void;
}

const TopContractsTable: React.FC<TopContractsTableProps> = ({ 
  contracts, 
  showValueFirst = false,
  showLocation = false,
  showDuration = false,
  showStatus = false,
  title = "Contracts",
  onRowClick
}) => {
  if (!contracts || contracts.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        No contracts available
      </div>
    );
  }

  const handleRowClick = (contractId: string) => {
    if (onRowClick && contractId) {
      onRowClick(contractId);
    }
  };

  // Format and sanitize buyer names
  const formatBuyerName = (buyer: any): string => {
    if (!buyer) return "Not specified";
    
    if (typeof buyer === 'string') {
      return buyer === "Unknown Buyer" ? "Not specified" : buyer;
    }
    
    // If buyer is an array, try to extract the first item
    if (Array.isArray(buyer)) {
      return buyer.length > 0 ? String(buyer[0]) : "Not specified";
    }
    
    // If buyer is an object with language keys
    if (typeof buyer === 'object') {
      // Check if it has a direct property called buyer_name
      if (buyer.buyer_name) return String(buyer.buyer_name);
      
      // Try to find any language property
      const langs = Object.keys(buyer);
      if (langs.length > 0) {
        const firstLang = buyer[langs[0]];
        if (Array.isArray(firstLang) && firstLang.length > 0) {
          return String(firstLang[0]);
        }
        if (firstLang) return String(firstLang);
      }
    }
    
    return "Not specified";
  };

  // Ensure all contracts have valid string properties and default values for missing data
  const safeContracts = contracts.map(contract => ({
    ...contract,
    title: typeof contract.title === 'string' ? contract.title : 'Untitled Contract',
    buyer: formatBuyerName(contract.buyer),
    country: typeof contract.country === 'string' ? contract.country : '',
    duration: typeof contract.duration === 'string' ? contract.duration : '',
    status: typeof contract.status === 'string' ? contract.status : '',
    cpv: typeof contract.cpv === 'string' ? contract.cpv : '',
    cpvDescription: typeof contract.cpvDescription === 'string' ? contract.cpvDescription : '',
  }));

  return (
    <div className="w-full">
      {title && (
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
      )}
      <div className="overflow-hidden">
        <div className="w-full overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {showValueFirst && (
                  <TableHead style={{ width: '20%' }}>Value</TableHead>
                )}
                <TableHead>Title</TableHead>
                {!showValueFirst && (
                  <TableHead style={{ width: '20%' }}>Value</TableHead>
                )}
                {showLocation && (
                  <TableHead style={{ width: '16%' }}>Location</TableHead>
                )}
                {showDuration && (
                  <TableHead style={{ width: '16%' }}>Duration</TableHead>
                )}
                {showStatus && (
                  <TableHead style={{ width: '16%' }}>Status</TableHead>
                )}
                <TableHead style={{ width: '16%' }}>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {safeContracts.map((contract, index) => (
                <TableRow 
                  key={index}
                  className={`border-b transition-colors hover:bg-muted/50 ${contract.id && onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={() => contract.id && onRowClick ? handleRowClick(contract.id) : undefined}
                >
                  {showValueFirst && (
                    <TableCell style={{ width: '20%' }} className="truncate">
                      {formatEuro(contract.value)}
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="font-medium line-clamp-1" title={contract.title}>
                      {contract.title || 'Untitled Contract'}
                    </div>
                    <div className="text-xs text-muted-foreground line-clamp-1">{contract.buyer}</div>
                    {contract.cpv && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="text-xs text-primary mt-1 cursor-help truncate">
                              CPV: {contract.cpv?.split(' ')[0]} {contract.cpvDescription && contract.cpvDescription !== "Unknown" ? 
                                `- ${contract.cpvDescription.length > 30 ? `${contract.cpvDescription.substring(0, 30)}...` : contract.cpvDescription}` 
                                : ''}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">{contract.cpv} {contract.cpvDescription ? `- ${contract.cpvDescription}` : ''}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </TableCell>
                  {!showValueFirst && (
                    <TableCell style={{ width: '20%' }} className="truncate">
                      {formatEuro(contract.value)}
                    </TableCell>
                  )}
                  {showLocation && (
                    <TableCell style={{ width: '16%' }} className="truncate">
                      {contract.country || '-'}
                    </TableCell>
                  )}
                  {showDuration && (
                    <TableCell style={{ width: '16%' }} className="truncate">
                      {contract.duration || '-'}
                    </TableCell>
                  )}
                  {showStatus && (
                    <TableCell style={{ width: '16%' }}>
                      {contract.status ? (
                        <Badge variant={
                          contract.status.toLowerCase() === 'completed' ? 'secondary' : 
                          contract.status.toLowerCase() === 'active' ? 'success' : 
                          contract.status.toLowerCase() === 'awarded' ? 'default' : 
                          'outline'
                        }>
                          {contract.status}
                        </Badge>
                      ) : '-'}
                    </TableCell>
                  )}
                  <TableCell style={{ width: '16%' }} className="truncate">
                    {contract.date ? safeFormatDate(contract.date) : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default TopContractsTable;
